import { Alert, Button, Card, Col, Empty, Flex, message, Row, Skeleton } from 'antd';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { BotInfo } from '~/services/workflow/types';
import { getBots, getTopBots } from '~/services/workflow';

import User_src from '/user_logo.jpg';

import Logo_src from '~/assets/images/logo2.png';
import { Eye, Check, SketchLogo, Rocket, ArrowRight } from '@phosphor-icons/react';
import { formatAmount } from '~/utils/function';
import { EllipsisTooltip } from '~/components/EllipsisTooltip';

import './index.less';

interface VideoRefs {
  [key: string]: HTMLVideoElement | null;
}

const ColPops = {
  xs: 24,
  md: 12,
  lg: 8,
  xl: 6,
};

const SKELETON_CARD_COUNT = 8;

function WorkflowBotCardSkeleton() {
  return (
    <Card
      className="workflow-bot-card workflow-bot-card--skeleton"
      hoverable={false}
      cover={
        <div className="workflow-bot-skeleton-cover">
          <Skeleton.Image active className="workflow-bot-skeleton-cover-img" />
        </div>
      }
    >
      <Skeleton active title={{ width: '72%' }} paragraph={{ rows: 1, width: ['100%', '90%'] }} />
      <Flex justify="space-between" align="center" gap={8} style={{ marginTop: 16 }}>
        <Flex align="center" gap={8}>
          <Skeleton.Avatar active size="small" />
          <Skeleton.Input active size="small" style={{ width: 88, height: 14 }} />
        </Flex>
        <Skeleton.Button active size="small" style={{ width: 88, height: 28 }} />
      </Flex>
    </Card>
  );
}

// 渲染智能体集合内容
const BotsList = ({ isTopBots = false }: { isTopBots?: boolean }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [botCards, setBotCards] = useState<BotInfo[]>([]);
  // 使用一个ref对象来管理多个视频实例
  const videoRefs = useRef<VideoRefs>({});

  // 为视频元素设置ref
  const setVideoRef = (id: string, el: HTMLVideoElement | null) => {
    videoRefs.current[id] = el;
  };

  // 处理鼠标进入视频区域
  const handleMouseEnter = (videoId: string) => {
    const video = videoRefs.current[videoId];
    if (video) {
      video.play().catch((error) => {
        // 处理自动播放失败，例如用户未交互时浏览器的策略
        // console.error('Autoplay failed:', error);
      });
    }
  };

  // 处理鼠标离开视频区域
  const handleMouseLeave = (videoId: string) => {
    const video = videoRefs.current[videoId];
    if (video) {
      video.pause();
      video.currentTime = 0; // 可选：重置播放进度
    }
  };

  const handleVideoClick = (botId: string) => {
    navigate(`/workflow/bot/${botId}`, { replace: true });
  };

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    void (async () => {
      try {
        if (isTopBots) {
          const { code, data } = await getTopBots();
          if (cancelled) return;
          if (code === 0) {
            setBotCards(data.list || []);
          }
        } else {
          const {
            code,
            data,
            message: msg,
          } = await getBots({
            page: 0,
            size: 100,
          });
          if (cancelled) return;
          if (code === 0) {
            setBotCards(data.list || []);
          } else {
            message.error('获取智能体数据失败: ' + msg);
          }
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isTopBots]);

  return (
    <>
      {loading ? (
        <>
          {Array.from({ length: SKELETON_CARD_COUNT }).map((_, i) => (
            <Col {...ColPops} key={`workflow-bot-skeleton-${i}`}>
              <WorkflowBotCardSkeleton />
            </Col>
          ))}
        </>
      ) : botCards.length > 0 ? (
        <>
          {botCards.map((card) => {
            const imageURL =
              card.examples.filter((example) => !!example.imageURL)[0]?.imageURL || card.iconURL;
            const videoURL = card.examples.filter((example) => !!example.videoURL)[0]?.videoURL;
            return (
              <Col {...ColPops} key={card.botID}>
                <Card
                  hoverable
                  onMouseEnter={() => handleMouseEnter(card.botID)}
                  onMouseLeave={() => handleMouseLeave(card.botID)}
                  cover={
                    <div className="workflow-bot-media">
                      {card.isSVIPFree && (
                        <div className="workflow-bot-media-left_badge">
                          <SketchLogo weight="fill" />
                          SVIP免费
                        </div>
                      )}
                      <Flex
                        align="center"
                        className={`workflow-bot-media-right_badge ${card.isUnlock ? 'purchased-badge' : 'price-badge'}`}
                      >
                        {card.isUnlock ? (
                          <>
                            <Check size={14} weight="bold" />
                            <span>已购买</span>
                          </>
                        ) : (
                          <>
                            {/* <Coins size={14} weight="fill" /> */}
                            <span>¥{formatAmount(card.price)}</span>
                            <span className="original-price">¥{formatAmount(card.price * 2)}</span>
                            <span className="reduced-percent">5折</span>
                          </>
                        )}
                      </Flex>
                      {imageURL ? (
                        <div className="workflow-bot-media-video-container">
                          <video
                            loop={true}
                            ref={(el) => setVideoRef(card.botID, el)}
                            preload="none"
                            muted
                            aria-label={`${card.name} 视频预览`}
                            className="workflow-bot-media-video-container-video"
                            poster={imageURL}
                          >
                            <source src={videoURL} type="video/mp4" />
                            您的浏览器不支持 HTML5 video 标签。
                          </video>
                          <Flex
                            className="workflow-bot-media-video-container-overlay"
                            align="center"
                            justify="center"
                            gap={8}
                            vertical
                          >
                            <Eye
                              weight="fill"
                              size={36}
                              className="workflow-bot-media-video-container-overlay-icon"
                            />
                            <span className="workflow-bot-media-video-container-overlay-text">
                              悬停播放预览
                            </span>
                          </Flex>
                        </div>
                      ) : (
                        <div className="workflow-bot-media-avatar-container">
                          <img
                            src={Logo_src}
                            alt={`${card.name || '智能体'} 封面`}
                            loading="lazy"
                            decoding="async"
                            className="workflow-bot-media-avatar-container-img"
                          />
                        </div>
                      )}
                    </div>
                  }
                  className="workflow-bot-card"
                  onClick={() => handleVideoClick(card.botID)}
                >
                  <EllipsisTooltip text={card.name} className="workflow-bot-title" />
                  <EllipsisTooltip text={card.description} className="workflow-bot-description" />
                  <Flex justify="space-between" gap={8}>
                    <Flex className="workflow-bot-creator ellipsis" align="middle" gap={8}>
                      <img
                        src={card.creatorHeadImg || User_src}
                        alt={card.creatorName || '简创AI团队'}
                        loading="lazy"
                        decoding="async"
                        className="workflow-bot-creator-img"
                      />
                      <span className="workflow-bot-creator-name ellipsis">{card.creatorName}</span>
                    </Flex>
                    <Button
                      type="primary"
                      shape="round"
                      size="small"
                      className="workflow-bot-card-button"
                    >
                      {card.workflows?.length || 0}个工作流
                    </Button>
                  </Flex>
                </Card>
              </Col>
            );
          })}
          {isTopBots && (
            <Col {...ColPops} key="more" onClick={() => navigate('/workflow')}>
              <Card className="workflow-bot-card workflow-bot-card-more">
                <div className="workflow-bot-card-more-bg">
                  <div className="workflow-bot-card-more-glow"></div>
                  <div className="workflow-bot-card-more-particles">
                    <div className="workflow-bot-card-more-particle particle-1"></div>
                    <div className="workflow-bot-card-more-particle particle-2"></div>
                    <div className="workflow-bot-card-more-particle particle-3"></div>
                    <div className="workflow-bot-card-more-particle particle-4"></div>
                  </div>
                </div>
                <div className="workflow-bot-card-more-content">
                  <div className="workflow-bot-card-more-icon">
                    <Rocket weight="fill" size={36} />
                  </div>
                  <h3 className="workflow-bot-card-more-title">获取更多优质工作流</h3>
                  <p className="workflow-bot-card-more-subtitle">探索无限可能，开启AI创作新体验</p>
                  <div className="workflow-bot-card-more-cta">
                    <span className="workflow-bot-card-more-text">立即前往</span>
                    <ArrowRight size={24} />
                  </div>
                </div>
              </Card>
            </Col>
          )}
        </>
      ) : (
        <div className="empty-state w-full mt-8">
          <Empty description="暂无智能体，点击右下角悬浮框联系客服" />
        </div>
      )}
    </>
  );
};

export default BotsList;
